import React, { useState } from 'react';
import { ArrowLeft, Save, RotateCcw, Plus, Eye, Edit2, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import DataTable from '../../../components/common/DataTable';
import CustomerPICModal from './CustomerPICModal';
import customerService from '../../../services/customerService';

const CustomerForm = ({ onBack, customerData = null, mode = 'add' }) => {
  // Customer Form State
  const [formData, setFormData] = useState({
    customerCode: customerData?.customerCode || '',
    customerName: customerData?.customerName || '',
    customerAddress: customerData?.customerAddress || '',
    type: customerData?.type || '',
    email: customerData?.email || '',
    description: customerData?.description || ''
  });

  // PIC Management State
  const [picList, setPicList] = useState(customerData?.pics || []);
  const [isPICModalOpen, setIsPICModalOpen] = useState(false);
  const [selectedPIC, setSelectedPIC] = useState(null);
  const [picModalMode, setPICModalMode] = useState('add');

  // Validation & Submit State
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // PIC Table Pagination & Sorting
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortConfig, setSortConfig] = useState({ sortBy: 'nama', direction: 'ASC' });
  const [searchValue, setSearchValue] = useState('');

  // Handle Customer Form Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate Customer Form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customerCode.trim()) {
      newErrors.customerCode = 'Customer Code is required';
    } else if (!/^[A-Z0-9-]+$/.test(formData.customerCode)) {
      newErrors.customerCode = 'Customer code must contain only uppercase letters, numbers, and hyphens';
    }
    
    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Customer Name is required';
    }
    
    if (!formData.customerAddress.trim()) {
      newErrors.customerAddress = 'Customer Address is required';
    }

    if (!formData.type.trim()) {
      newErrors.type = 'Type is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle Customer Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const customerPayload = {
        customerCode: formData.customerCode,
        customerName: formData.customerName,
        customerAddress: formData.customerAddress,
        type: formData.type,
        email: formData.email || null,
        description: formData.description || null,
        pics: picList.map(pic => ({
          id: pic.id || null,
          nama: pic.nama,
          email: pic.email || null,
          departemen: pic.departemen || null,
          jabatan: pic.jabatan || null,
          phone: pic.phone,
          remarks: pic.remarks || null
        }))
      };
      
      if (mode === 'add') {
        await customerService.createCustomer(customerPayload);
        toast.success('Customer created successfully!');
      } else {
        await customerService.updateCustomer(customerData.id, customerPayload);
        toast.success('Customer updated successfully!');
      }
      
      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // Error already handled by axios interceptor with toast
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Form Reset
  const handleReset = () => {
    setFormData({
      customerCode: '',
      customerName: '',
      customerAddress: '',
      type: '',
      email: '',
      description: ''
    });
    setPicList([]);
    setErrors({});
  };

  // ===== PIC MANAGEMENT HANDLERS =====

  // Open Add PIC Modal
  const handleAddPIC = () => {
    setSelectedPIC(null);
    setPICModalMode('add');
    setIsPICModalOpen(true);
  };

  // Open View PIC Modal
  const handleViewPIC = (pic) => {
    setSelectedPIC(pic);
    setPICModalMode('view');
    setIsPICModalOpen(true);
  };

  // Open Edit PIC Modal
  const handleEditPIC = (pic) => {
    setSelectedPIC(pic);
    setPICModalMode('edit');
    setIsPICModalOpen(true);
  };

  // Delete PIC
  const handleDeletePIC = (pic) => {
    if (window.confirm(`Are you sure you want to remove ${pic.nama} from PIC list?`)) {
      setPicList(prev => prev.filter(p => p.tempId !== pic.tempId && p.id !== pic.id));
      toast.success('PIC removed from list');
    }
  };

  // Save PIC (from modal)
  const handleSavePIC = (picData) => {
    if (picModalMode === 'add') {
      // Add new PIC with temporary ID
      const newPic = {
        ...picData,
        tempId: `temp-${Date.now()}`
      };
      setPicList(prev => [...prev, newPic]);
      toast.success('PIC added to list');
    } else if (picModalMode === 'edit') {
      // Update existing PIC
      setPicList(prev => prev.map(p => {
        if (p.id && p.id === selectedPIC.id) {
          return { ...picData, id: selectedPIC.id, tempId: selectedPIC.tempId };
        }
        if (p.tempId && p.tempId === selectedPIC.tempId) {
          return { ...picData, tempId: selectedPIC.tempId };
        }
        return p;
      }));
      toast.success('PIC updated');
    }
    setIsPICModalOpen(false);
  };

  // Filter PIC based on search
  const filteredPICs = picList.filter(pic =>
    pic.nama.toLowerCase().includes(searchValue.toLowerCase()) ||
    (pic.email && pic.email.toLowerCase().includes(searchValue.toLowerCase())) ||
    (pic.departemen && pic.departemen.toLowerCase().includes(searchValue.toLowerCase())) ||
    (pic.jabatan && pic.jabatan.toLowerCase().includes(searchValue.toLowerCase())) ||
    pic.phone.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Sort PICs
  const sortedPICs = [...filteredPICs].sort((a, b) => {
    const aValue = a[sortConfig.sortBy] || '';
    const bValue = b[sortConfig.sortBy] || '';
    
    if (aValue < bValue) return sortConfig.direction === 'ASC' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ASC' ? 1 : -1;
    return 0;
  });

  // Paginate PICs
  const startIndex = currentPage * pageSize;
  const paginatedPICs = sortedPICs.slice(startIndex, startIndex + pageSize);

  // Define PIC Table Columns
  const picColumns = [
    {
      key: 'no',
      label: 'No',
      sortable: false,
      width: 'w-16',
      render: (row, index) => (
        <span className="font-medium text-gray-900">
          {startIndex + index + 1}
        </span>
      )
    },
    {
      key: 'nama',
      label: 'Nama',
      sortable: true,
      render: (row) => (
        <span className="font-semibold text-gray-900">{row.nama}</span>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (row) => (
        <span className="text-gray-700">{row.email || '-'}</span>
      )
    },
    {
      key: 'departemen',
      label: 'Departemen',
      sortable: true,
      render: (row) => (
        <span className="text-gray-700">{row.departemen || '-'}</span>
      )
    },
    {
      key: 'jabatan',
      label: 'Jabatan',
      sortable: true,
      render: (row) => (
        <span className="text-gray-700">{row.jabatan || '-'}</span>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => (
        <span className="text-gray-700">{row.phone}</span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => handleViewPIC(row)}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="View Details"
            type="button"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEditPIC(row)}
            className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition-colors"
            title="Edit"
            type="button"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeletePIC(row)}
            className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Delete"
            type="button"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to list"
              type="button"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {mode === 'add' ? 'Add New Customer' : 'Edit Customer'}
              </h1>
              <p className="text-gray-600 mt-1">
                {mode === 'add' 
                  ? 'Fill in the form below to create a new customer' 
                  : 'Update customer information'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <form onSubmit={handleSubmit}>
          {/* Customer Information Section */}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-gray-800">Customer Information</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Code */}
              <div>
                <label htmlFor="customerCode" className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Code <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="customerCode"
                  name="customerCode"
                  value={formData.customerCode}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.customerCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., TSEL, IOH, XLS"
                  disabled={isSubmitting || mode === 'edit'}
                />
                {errors.customerCode && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.customerCode}
                  </p>
                )}
              </div>

              {/* Type */}
              <div>
                <label htmlFor="type" className="block text-sm font-semibold text-gray-700 mb-2">
                  Type <span className="text-red-600">*</span>
                </label>
                <select
                  id="type"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.type ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                >
                  <option value="">Select Type</option>
                  <option value="Telecom Operator">Telecom Operator</option>
                  <option value="Tower Company">Tower Company</option>
                  <option value="ISP Provider">ISP Provider</option>
                  <option value="Government Agency">Government Agency</option>
                  <option value="Enterprise">Enterprise</option>
                  <option value="Other">Other</option>
                </select>
                {errors.type && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.type}
                  </p>
                )}
              </div>

              {/* Customer Name */}
              <div className="md:col-span-2">
                <label htmlFor="customerName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Name <span className="text-red-600">*</span>
                </label>
                <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.customerName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., PT Telekomunikasi Selular"
                  disabled={isSubmitting}
                />
                {errors.customerName && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.customerName}
                  </p>
                )}
              </div>

              {/* Customer Address */}
              <div className="md:col-span-2">
                <label htmlFor="customerAddress" className="block text-sm font-semibold text-gray-700 mb-2">
                  Customer Address <span className="text-red-600">*</span>
                </label>
                <textarea
                  id="customerAddress"
                  name="customerAddress"
                  value={formData.customerAddress}
                  onChange={handleChange}
                  rows="4"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none ${
                    errors.customerAddress ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter full customer address"
                  disabled={isSubmitting}
                />
                {errors.customerAddress && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.customerAddress}
                  </p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                    errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., contact@example.com"
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span> {errors.email}
                  </p>
                )}
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-semibold text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all resize-none"
                  placeholder="Optional description about the customer"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gray-200"></div>

          {/* PIC Management Section */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-red-600 to-red-500 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Person In Charge (PIC)</h2>
                  <p className="text-xs text-gray-500">Manage customer contact persons</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleAddPIC}
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-4 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                disabled={isSubmitting}
              >
                <Plus className="w-4 h-4" />
                Add PIC
              </button>
            </div>

            {/* PIC DataTable */}
            <DataTable
              columns={picColumns}
              data={paginatedPICs}
              currentPage={currentPage}
              pageSize={pageSize}
              totalElements={filteredPICs.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setCurrentPage(0);
              }}
              onSort={setSortConfig}
              sortConfig={sortConfig}
              isLoading={false}
              onSearch={(value) => {
                setSearchValue(value);
                setCurrentPage(0);
              }}
              searchValue={searchValue}
              searchPlaceholder="Search by name, email, department, position, or phone..."
              enableQuickJump={false}
              customPageSizes={[5, 10, 25]}
              striped={false}
              compact={true}
              maxHeight="400px"
              headerBgColor="bg-gradient-to-r from-red-600 to-red-500"
              headerTextColor="text-white"
              hoverColor="hover:bg-red-50"
            />
          </div>

          {/* Form Actions */}
          <div className="p-6 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleReset}
                className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors flex items-center gap-2"
                disabled={isSubmitting}
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
              <button
                type="button"
                onClick={onBack}
                className="border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium px-6 py-2 rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-medium px-6 py-2 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <Save className="w-5 h-5" />
                {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Create Customer' : 'Update Customer')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* PIC Modal */}
      <CustomerPICModal
        isOpen={isPICModalOpen}
        onClose={() => setIsPICModalOpen(false)}
        onSave={handleSavePIC}
        picData={selectedPIC}
        mode={picModalMode}
      />
    </div>
  );
};

export default CustomerForm;